import { useAuthStore } from '@supply-mover-2-0/core';
import type { SoftphoneDialerEntryDto } from '@supply-mover-2-0/shared';
import { create } from 'zustand';
import { SoftphoneDialerQueryService } from '../services/SoftphoneDialerQueryService';

/**
 * How long a loaded favorites list is trusted before the Favorites tab asks
 * again. Long enough that switching tabs back and forth shares one request;
 * short enough that a number pinned on another device shows up without a
 * reload.
 */
export const DIALER_FAVORITES_MAX_AGE_MS = 60_000;

export type DialerFavoritesLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface DialerFavoritesStoreState {
	/** Whose favorites are held, as `<tenant>:<user>`. Null = nothing loaded. */
	ownerKey: string | null;
	status: DialerFavoritesLoadStatus;
	/** When the list was last loaded or replaced by a save (epoch ms); 0 = never. */
	loadedAt: number;
	/** The rep's favorites, in their order. */
	favorites: ReadonlyArray<SoftphoneDialerEntryDto>;
	/**
	 * Every number known to be a favorite: the loaded list, plus, until the
	 * list has loaded, the stars the Recents and Frequent rows arrived with.
	 * Every star on screen reads this, so toggling one updates them all.
	 */
	favoriteNumbers: ReadonlySet<string>;
	/** Numbers with a pin or unpin in flight. */
	savingNumbers: ReadonlySet<string>;
}

export interface DialerFavoritesStoreActions {
	/** Load the favorites list unless a recent copy is held. Callers share one request. */
	ensureLoaded: () => Promise<void>;
	/**
	 * Record the stars a list arrived with (Recents, Frequent). Ignored once
	 * the favorites list itself has loaded: that list, kept current by every
	 * pin and unpin, is the truth from then on.
	 */
	noteEntries: (entries: ReadonlyArray<SoftphoneDialerEntryDto>) => void;
	/**
	 * Pin or unpin a number. The star changes at once and is put back if the
	 * save fails. Resolves true when it was saved.
	 */
	setFavorite: (
		entry: SoftphoneDialerEntryDto,
		isFavorite: boolean,
	) => Promise<boolean>;
	reset: () => void;
}

const initialState: DialerFavoritesStoreState = {
	ownerKey: null,
	status: 'idle',
	loadedAt: 0,
	favorites: [],
	favoriteNumbers: new Set<string>(),
	savingNumbers: new Set<string>(),
};

/**
 * Who the favorites belong to. A different tenant, a different user, or a
 * delegated session (which swaps the user) all change the key, so one rep's
 * favorites are never shown to another.
 */
function currentOwnerKey(): string | null {
	const { user, instance } = useAuthStore.getState();
	if (user == null) {
		return null;
	}
	return `${instance?.alias ?? ''}:${String(user.instanceUserId ?? user.id)}`;
}

function withNumber(
	numbers: ReadonlySet<string>,
	phoneE164: string,
	isPresent: boolean,
): ReadonlySet<string> {
	const next = new Set(numbers);
	if (isPresent) {
		next.add(phoneE164);
	} else {
		next.delete(phoneE164);
	}
	return next;
}

/** The favorites with one number added at the end, or removed. */
export function withFavoriteEntry(
	favorites: ReadonlyArray<SoftphoneDialerEntryDto>,
	entry: SoftphoneDialerEntryDto,
	isFavorite: boolean,
): Array<SoftphoneDialerEntryDto> {
	const others = favorites.filter(
		(favorite) => favorite.phoneE164 !== entry.phoneE164,
	);
	return isFavorite ? [...others, { ...entry, isFavorite: true }] : others;
}

// The request shared by every caller that asked while it was in flight.
let loadInFlight: Promise<void> | null = null;

/**
 * PD-14960 — the signed-in rep's favorite numbers, shared by the dialer's
 * Favorites tab and every star on its Recents and Frequent rows.
 *
 * Not persisted: the rows live on the server so they follow the rep across
 * devices, and a stale copy from an earlier session would only show the
 * wrong stars until the first load finished.
 */
export const useSoftphoneDialerFavoritesStore = create<
	DialerFavoritesStoreState & DialerFavoritesStoreActions
>()((set, get) => {
	/** Switch to the current rep's (empty) state when the rep changed. */
	const claimOwner = (): string | null => {
		const ownerKey = currentOwnerKey();
		if (ownerKey != null && get().ownerKey !== ownerKey) {
			loadInFlight = null;
			set({ ...initialState, ownerKey });
		}
		return ownerKey;
	};

	return {
		...initialState,

		ensureLoaded: async () => {
			const ownerKey = claimOwner();
			if (ownerKey == null) {
				return;
			}

			const state = get();
			if (
				state.status === 'ready' &&
				Date.now() - state.loadedAt < DIALER_FAVORITES_MAX_AGE_MS
			) {
				return;
			}
			if (loadInFlight != null) {
				return loadInFlight;
			}

			// A refresh keeps the list on screen while the new one loads.
			if (state.status !== 'ready') {
				set({ status: 'loading' });
			}

			const request = (async (): Promise<void> => {
				try {
					const favorites = await SoftphoneDialerQueryService.getFavorites();
					if (get().ownerKey !== ownerKey) {
						return;
					}
					set({
						status: 'ready',
						loadedAt: Date.now(),
						favorites,
						favoriteNumbers: new Set(
							favorites.map((favorite) => favorite.phoneE164),
						),
					});
				} catch {
					if (get().ownerKey === ownerKey && get().status !== 'ready') {
						set({ status: 'error' });
					}
				} finally {
					loadInFlight = null;
				}
			})();
			loadInFlight = request;
			return request;
		},

		noteEntries: (entries) => {
			claimOwner();
			const state = get();
			if (state.status === 'ready' || entries.length === 0) {
				return;
			}
			const next = new Set(state.favoriteNumbers);
			for (const entry of entries) {
				// A number with a save in flight keeps the rep's choice.
				if (state.savingNumbers.has(entry.phoneE164)) {
					continue;
				}
				if (entry.isFavorite) {
					next.add(entry.phoneE164);
				} else {
					next.delete(entry.phoneE164);
				}
			}
			set({ favoriteNumbers: next });
		},

		setFavorite: async (entry, isFavorite) => {
			const ownerKey = claimOwner();
			const phoneE164 = entry.phoneE164;
			const before = get();
			const previousIndex = before.favorites.findIndex(
				(favorite) => favorite.phoneE164 === phoneE164,
			);
			const previousEntry =
				previousIndex >= 0 ? before.favorites[previousIndex] : null;

			set({
				favorites: withFavoriteEntry(before.favorites, entry, isFavorite),
				favoriteNumbers: withNumber(
					before.favoriteNumbers,
					phoneE164,
					isFavorite,
				),
				savingNumbers: withNumber(before.savingNumbers, phoneE164, true),
			});

			try {
				const favorites = isFavorite
					? await SoftphoneDialerQueryService.addFavorite({
							phoneE164,
							label: entry.capturedName ?? null,
						})
					: await SoftphoneDialerQueryService.removeFavorite(phoneE164);
				if (get().ownerKey === ownerKey) {
					// The server answers with the whole list, so from here it is
					// the truth even if the Favorites tab never loaded.
					set({
						status: 'ready',
						loadedAt: Date.now(),
						favorites,
						favoriteNumbers: new Set(
							favorites.map((favorite) => favorite.phoneE164),
						),
					});
				}
				return true;
			} catch {
				if (get().ownerKey === ownerKey) {
					// Put back only this number, so a save of another number that
					// finished meanwhile is kept.
					const current = get();
					let favorites = current.favorites.filter(
						(favorite) => favorite.phoneE164 !== phoneE164,
					);
					if (!isFavorite && previousEntry != null) {
						favorites = [...favorites];
						favorites.splice(
							Math.min(previousIndex, favorites.length),
							0,
							previousEntry,
						);
					}
					set({
						favorites,
						favoriteNumbers: withNumber(
							current.favoriteNumbers,
							phoneE164,
							!isFavorite,
						),
					});
				}
				return false;
			} finally {
				set({
					savingNumbers: withNumber(get().savingNumbers, phoneE164, false),
				});
			}
		},

		reset: () => {
			loadInFlight = null;
			set({ ...initialState });
		},
	};
});
